import * as React from 'react';
import { Link } from 'react-router-dom';

import { Picture } from '@shared/components/helpers';
import { ImgixBG, ImgixCrop, ImgixFit } from '@shared/utils/imgix';

import { NAV_BAR_HEIGHT } from '@portal/components/shared/layout';
import { ItemStatusSlug, buildClaimURL, itemURL, itemsURL } from '@portal/config/routes';
import { SANS_SERIF_FONT_FAMILY } from '@portal/constants/fonts';
import unsafeToReturnErrorIcon from '@portal/images/icons/unsafe_to_return_error.png';
import {
  useItemsWithConditionNotesQuery,
  useConditionNotesAcknowledgeMutation,
  InventoryDocument,
  ImageFragment,
  ImageState,
  ItemsWithConditionNotesQuery,
} from '@portal/schema';

import { Detail } from '../detail/detail';
import { ItemViewContextProvider, useItemViewContext } from '../item_view_context';
import { ConditionNotesPhotoGalleryDialog } from './condition_notes_photo_gallery_dialog';

const PER = 48;

/** Figma 18:730 — hex tokens only (no design-system imports). */
const C = {
  cloud: '#ffffff',
  panther: '#000000',
  storm: '#4e4e4e',
  accent: '#066b65',
  primary: '#097e77',
  photoBg: '#d7d7d7',
  unsafe: '#be0706',
} as const;

const SERIF = "'Recoleta', Georgia, serif";

/** ~75% of Figma 18:730 layout numbers. */
const SCALE = 0.75;
const z = (n: number) => Math.round(n * SCALE);

const W = {
  pageMax: z(1280),
  contentMax: z(972),
  photoH: z(167),
  gapIntroList: z(60),
  gapSection: z(80),
  gapSectionHead: z(24),
  gapCards: z(24),
  gapPhotoCopy: z(36),
  gapLines: z(13),
  back: z(36),
  gapBackTitle: z(30),
  title: z(45),
  sectionTitle: z(32),
  body: z(24),
  lh: 1.2,
  pagePadY: z(24),
  pagePadX: z(24),
  pagePadBottom: z(64),
  pagePadLgT: z(38),
  pagePadLgX: z(60),
  backFont: z(28),
  thumbRadius: z(8),
  badgeTop: z(10),
  badgeRight: z(14),
  badgeSize: z(24),
  badgeIcon: z(20),
  moreWrapMt: z(24),
  morePadY: z(10),
  morePadX: z(24),
  moreRadius: z(4),
  unsafeFont: z(18),
  unsafeIcon: z(23),
  unsafeGap: z(6),
} as const;

const CSS = `
.cn-shell {
  display: flex;
  position: absolute;
  left: 0;
  width: 100%;
  top: ${NAV_BAR_HEIGHT}px;
  height: calc(100% - ${NAV_BAR_HEIGHT}px);
  z-index: 0;
  align-items: stretch;
}
.cn-page {
  box-sizing: border-box;
  flex: 1;
  min-width: 0;
  width: 100%;
  max-width: ${W.pageMax}px;
  margin: 0 auto;
  overflow: auto;
  background: ${C.cloud};
  padding: ${W.pagePadY}px ${W.pagePadX}px ${W.pagePadBottom}px;
  font-family: ${SANS_SERIF_FONT_FAMILY};
  font-size: ${W.body}px;
  color: ${C.panther};
}
@media (min-width: 1280px) {
  .cn-page {
    max-width: none;
    padding: ${W.pagePadLgT}px ${W.pagePadLgX}px ${W.pagePadBottom}px;
  }
  .cn-page--sidebar {
    padding-right: 116px;
  }
}
@media (max-width: 1279px) {
  .cn-page--sidebar { display: none; }
}
.cn-back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${W.back}px;
  height: ${W.back}px;
  margin-bottom: ${W.gapBackTitle}px;
  color: ${C.accent};
  text-decoration: none;
  font-size: ${W.backFont}px;
  line-height: 1;
}
.cn-back:hover { opacity: 0.85; }
.cn-inner {
  max-width: ${W.contentMax}px;
  margin: 0 auto;
}
.cn-stack {
  display: flex;
  flex-direction: column;
  gap: ${W.gapIntroList}px;
  align-items: flex-start;
}
.cn-title {
  margin: 0;
  font-family: ${SERIF};
  font-weight: 500;
  font-size: ${W.title}px;
  line-height: ${W.lh};
  letter-spacing: 0;
  color: ${C.panther};
}
.cn-muted { margin: 0; color: ${C.storm}; }
.cn-sections {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${W.gapSection}px;
}
.cn-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${W.gapSectionHead}px;
}
.cn-sectionTitle {
  margin: 0;
  font-family: ${SERIF};
  font-weight: 500;
  font-size: ${W.sectionTitle}px;
  line-height: ${W.lh};
  color: ${C.panther};
}
.cn-sectionHead {
  display: flex;
  flex-direction: column;
  gap: ${z(18)}px;
}
.cn-claimLink {
  color: ${C.primary};
  font-weight: 500;
  font-size: ${W.body}px;
  line-height: ${W.lh};
  text-decoration: underline;
  text-underline-position: from-font;
}
.cn-claimLink:hover { opacity: 0.85; }
.cn-grid {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: ${W.gapCards}px;
  box-sizing: border-box;
}
@media (min-width: 720px) {
  .cn-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (min-width: 1100px) {
  .cn-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
.cn-card {
  display: flex;
  flex-direction: column;
  gap: ${W.gapPhotoCopy}px;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
}
.cn-thumb {
  position: relative;
  width: 100%;
  height: ${W.photoH}px;
  box-sizing: border-box;
  border-radius: ${W.thumbRadius}px;
  overflow: hidden;
  background: ${C.photoBg};
  line-height: 0;
}
.cn-thumb--go { cursor: pointer; }
.cn-thumb img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cn-thumbPh {
  width: 100%;
  height: 100%;
  background: ${C.photoBg};
}
.cn-badge {
  position: absolute;
  top: ${W.badgeTop}px;
  right: ${W.badgeRight}px;
  width: ${W.badgeSize}px;
  height: ${W.badgeSize}px;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cn-lines {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${W.gapLines}px;
  font-size: ${W.body}px;
  line-height: ${W.lh};
}
.cn-lines p { margin: 0; width: 100%; }
.cn-lines strong { font-weight: 500; }
.cn-itemLink {
  color: ${C.panther};
  font-weight: 500;
  text-decoration: underline;
  text-underline-position: from-font;
}
.cn-itemLink:hover { opacity: 0.85; }
.cn-unsafe {
  display: inline-flex;
  align-items: center;
  gap: ${W.unsafeGap}px;
  padding: ${W.unsafeGap}px 0;
  color: ${C.unsafe};
  font-weight: 500;
  font-size: ${W.unsafeFont}px;
  line-height: ${W.lh};
}
.cn-unsafeIcon {
  width: ${W.unsafeIcon}px;
  height: ${W.unsafeIcon}px;
  flex-shrink: 0;
}
.cn-moreWrap {
  margin-top: ${W.moreWrapMt}px;
  width: 100%;
  display: flex;
  justify-content: center;
}
.cn-more {
  font: inherit;
  cursor: pointer;
  padding: ${W.morePadY}px ${W.morePadX}px;
  border: 1px solid ${C.accent};
  border-radius: ${W.moreRadius}px;
  background: ${C.cloud};
  color: ${C.accent};
}
.cn-more:disabled { opacity: 0.5; cursor: default; }
`;

type Row = ItemsWithConditionNotesQuery['paginatedItemsWithConditionNotes']['results'][number];
type Docs = NonNullable<
  | (Row & { __typename: 'Item' })['beforePickupDamageDocumentation']
  | (Row & { __typename: 'ItemGroup' })['beforePickupDamageDocumentation']
>;

type DamageKind = 'before_pickup' | 'after_pickup';

/** Groups: name or category like item cards. Items: name or category plus `#last4` when barcode exists. */
function itemDisplayLine(item: Row): string {
  if (item.__typename === 'ItemGroup') {
    return item.name?.trim() || item.categoryName?.trim() || 'Item';
  }
  const rawBarcode = item.barcode.value.trim();
  const last4 = rawBarcode.length >= 4 ? rawBarcode.slice(-4) : rawBarcode.length > 0 ? rawBarcode : '';

  const trimmedName = item.name?.trim();
  if (trimmedName) {
    return last4 ? `${trimmedName} #${last4}` : trimmedName;
  }

  const cat = item.categoryName?.trim() || 'Item';
  return last4 ? `${cat} #${last4}` : cat;
}

function docsFor(item: Row, kind: DamageKind): Docs | null {
  if (kind === 'before_pickup') {
    return item.beforePickupDamageDocumentation ?? null;
  }
  return item.afterPickupDamageDocumentation ?? null;
}

function pickThumb(item: Row, docs: Docs): { src: string | null; galleryStart: number } {
  const docsImages = docs.images;
  const uploadedIdx = docsImages.findIndex((img) => img.state === ImageState.Uploaded && img.source);
  if (uploadedIdx >= 0) {
    return { src: docsImages[uploadedIdx].source, galleryStart: uploadedIdx };
  }
  if (docsImages[0]?.source) {
    return { src: docsImages[0].source, galleryStart: 0 };
  }
  if (item.__typename === 'ItemGroup' && item.heroImage?.source) {
    return { src: item.heroImage.source, galleryStart: 0 };
  }
  if (item.__typename === 'Item' && item.images[0]?.source) {
    return { src: item.images[0].source, galleryStart: 0 };
  }
  return { src: null, galleryStart: 0 };
}

function PhotoBadge(): React.ReactElement {
  const s = W.badgeIcon;
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={s} height={s} viewBox="0 0 20 20" fill="none" aria-hidden>
      <path
        d="M6 16C5.45 16 4.97917 15.8042 4.5875 15.4125C4.19583 15.0208 4 14.55 4 14V2C4 1.45 4.19583 0.979167 4.5875 0.5875C4.97917 0.195833 5.45 0 6 0H18C18.55 0 19.0208 0.195833 19.4125 0.5875C19.8042 0.979167 20 1.45 20 2V14C20 14.55 19.8042 15.0208 19.4125 15.4125C19.0208 15.8042 18.55 16 18 16H6ZM2 20C1.45 20 0.979167 19.8042 0.5875 19.4125C0.195833 19.0208 0 18.55 0 18V4H2V18H16V20H2Z"
        fill="white"
      />
    </svg>
  );
}

function PhotoThumb(props: { hasGallery: boolean; src: string | null; onOpen(): void }): React.ReactElement {
  const { hasGallery, src, onOpen } = props;
  const [bad, setBad] = React.useState(false);
  React.useEffect(() => {
    setBad(false);
  }, [src]);

  const show = Boolean(src) && !bad;

  const onKey = (e: React.KeyboardEvent) => {
    if (!hasGallery) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen();
    }
  };

  return (
    <div
      className={`cn-thumb${hasGallery ? ' cn-thumb--go' : ''}`}
      role={hasGallery ? 'button' : undefined}
      tabIndex={hasGallery ? 0 : undefined}
      onClick={hasGallery ? onOpen : undefined}
      onKeyDown={hasGallery ? onKey : undefined}
      aria-label={hasGallery ? 'View condition photos' : undefined}
    >
      {show ? (
        <Picture
          source={src as string}
          width={324}
          height={W.photoH}
          background={ImgixBG.Gray}
          fit={ImgixFit.Crop}
          crop={ImgixCrop.Focalpoint}
          alt=""
          onError={() => setBad(true)}
        />
      ) : (
        <div className="cn-thumbPh" aria-label="No preview photo available" role="img" />
      )}
      <span className="cn-badge" aria-hidden>
        <PhotoBadge />
      </span>
    </div>
  );
}

function UnsafeToReturnBadge(): React.ReactElement {
  return (
    <p className="cn-unsafe">
      <img className="cn-unsafeIcon" src={unsafeToReturnErrorIcon} alt="" aria-hidden />
      Unsafe to return
    </p>
  );
}

function ConditionCard(props: {
  item: Row;
  kind: DamageKind;
  onOpenPhotos(images: ImageFragment[], start: number): void;
}): React.ReactElement | null {
  const { item, kind, onOpenPhotos } = props;
  const { setSelectedItem } = useItemViewContext();
  const docs = docsFor(item, kind);
  if (!docs) return null;

  const itemLine = itemDisplayLine(item);
  const typeLabel = kind === 'before_pickup' ? 'Condition' : 'Type';
  const typeLine = docs.damageTypeLabels.length ? docs.damageTypeLabels.join(', ') : '—';
  const locLine = docs.damageLocationLabels.length ? docs.damageLocationLabels.join(', ') : '—';
  const images = docs.images;
  const { src: thumbSrc, galleryStart } = pickThumb(item, docs);
  const hasGallery = images.length > 0;
  const showUnsafeToReturn = kind === 'after_pickup' && docs.unsafeToReturn;

  return (
    <article className="cn-card">
      <PhotoThumb
        hasGallery={hasGallery}
        src={thumbSrc}
        onOpen={() => {
          if (hasGallery) onOpenPhotos(images, galleryStart);
        }}
      />
      <div className="cn-lines">
        <p>
          <Link
            to={itemURL(item.uuid)}
            className="cn-itemLink"
            onClick={(e) => {
              // Keep the inventory URL for open-in-new-tab, but open the sidebar here.
              e.preventDefault();
              setSelectedItem({ __typename: item.__typename, uuid: item.uuid });
            }}
          >
            {itemLine}
          </Link>
        </p>
        {showUnsafeToReturn ? <UnsafeToReturnBadge /> : null}
        <p>
          <strong>{typeLabel}:</strong>
          {` ${typeLine}`}
        </p>
        <p>
          <strong>Location:</strong>
          {` ${locLine}`}
        </p>
      </div>
    </article>
  );
}

function ConditionSection(props: {
  title: string;
  kind: DamageKind;
  rows: Row[];
  claimLink?: boolean;
  onOpenPhotos(images: ImageFragment[], start: number): void;
}): React.ReactElement | null {
  const { title, kind, rows, claimLink, onOpenPhotos } = props;
  const cards = rows.filter((row) => docsFor(row, kind));
  if (cards.length === 0) return null;

  return (
    <section className="cn-section">
      <div className="cn-sectionHead">
        <h2 className="cn-sectionTitle">{title}</h2>
        {claimLink ? (
          <Link to={buildClaimURL()} className="cn-claimLink">
            Submit a claim
          </Link>
        ) : null}
      </div>
      <div className="cn-grid">
        {cards.map((row) => (
          <ConditionCard key={`${kind}-${row.uuid}`} item={row} kind={kind} onOpenPhotos={onOpenPhotos} />
        ))}
      </div>
    </section>
  );
}

const ConditionNotesSummaryInner: React.FC = () => {
  const { selectedItem } = useItemViewContext();
  const { data, loading, error, fetchMore } = useItemsWithConditionNotesQuery({
    variables: { page: 1, per: PER },
  });

  const [ackConditionNotesView] = useConditionNotesAcknowledgeMutation({
    refetchQueries: [{ query: InventoryDocument }],
  });
  const viewAckSentRef = React.useRef(false);

  const [gallery, setGallery] = React.useState<{
    images: ImageFragment[];
    start: number;
  } | null>(null);
  const [loadMoreLoading, setLoadMoreLoading] = React.useState(false);

  React.useEffect(() => {
    if (!gallery) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [gallery]);

  React.useEffect(() => {
    if (!data?.paginatedItemsWithConditionNotes || viewAckSentRef.current) return;
    viewAckSentRef.current = true;
    void ackConditionNotesView();
  }, [data, ackConditionNotesView]);

  if (error) throw error;

  if (loading && !data) {
    return (
      <>
        <style>{CSS}</style>
        <div className="cn-shell">
          <main className="cn-page">
            <p className="cn-muted">Loading…</p>
          </main>
          <Detail />
        </div>
      </>
    );
  }

  const paged = data?.paginatedItemsWithConditionNotes;
  const rows = paged?.results ?? [];
  const nextPage = paged?.pagination.nextPage;
  const total = paged?.pagination.totalCount ?? 0;
  const openPhotos = (images: ImageFragment[], start: number) => setGallery({ images, start });

  return (
    <>
      <style>{CSS}</style>
      <div className="cn-shell">
        <main className={`cn-page${selectedItem ? ' cn-page--sidebar' : ''}`}>
          <div className="cn-inner">
            <Link to={itemsURL(ItemStatusSlug.Available)} className="cn-back" aria-label="Back">
              ←
            </Link>

            <div className="cn-stack">
              <h1 className="cn-title">Items for your review</h1>

              {total === 0 && !loading ? (
                <p className="cn-muted">You don&apos;t have any documented conditions on file right now.</p>
              ) : null}

              <div className="cn-sections">
                <ConditionSection
                  title="Conditions identified before pickup"
                  kind="before_pickup"
                  rows={rows}
                  onOpenPhotos={openPhotos}
                />
                <ConditionSection
                  title="Conditions identified after pickup"
                  kind="after_pickup"
                  rows={rows}
                  claimLink
                  onOpenPhotos={openPhotos}
                />
              </div>

              {nextPage ? (
                <div className="cn-moreWrap">
                  <button
                    type="button"
                    className="cn-more"
                    disabled={loadMoreLoading}
                    onClick={() => {
                      if (loadMoreLoading) return;
                      setLoadMoreLoading(true);
                      fetchMore({
                        variables: { page: nextPage, per: PER },
                        updateQuery: (prev, { fetchMoreResult }) => {
                          if (!fetchMoreResult) return prev;
                          const a = prev.paginatedItemsWithConditionNotes;
                          const b = fetchMoreResult.paginatedItemsWithConditionNotes;
                          return {
                            ...prev,
                            paginatedItemsWithConditionNotes: {
                              ...b,
                              results: [...a.results, ...b.results],
                            },
                          };
                        },
                      }).finally(() => setLoadMoreLoading(false));
                    }}
                  >
                    {loadMoreLoading ? 'Loading…' : 'Load more'}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </main>
        <Detail />
      </div>

      <ConditionNotesPhotoGalleryDialog
        open={!!gallery}
        images={gallery?.images ?? []}
        startIndex={gallery?.start ?? 0}
        onClose={() => setGallery(null)}
      />
    </>
  );
};

export const ConditionNotesSummary: React.FC = () => (
  <ItemViewContextProvider>
    <ConditionNotesSummaryInner />
  </ItemViewContextProvider>
);
